import { ClassType } from '../../../api';
import { TSession } from '../Sessions';
import { PeriodTypeToString } from '../SessionPeriods';
import { LabelTask, Period, timeSpanToSeconds } from '../../../services/sessionModels';
import _ from 'underscore';
import { secondsToHms } from '../../Util/TimeInputField';

export type TRow = {
  id: string;
  period: string;
  team: string | undefined;
  progress: number;
  type: string;
  startTime: string;
  endTime: string;
  identity: string;
  owner: UserFragment | null;
  tlt: LabelTask;
};

export enum EvictionStatus {
  Available = "available",
  Evicting = "evicting",
  Evicted = "evicted",
  Failed = "eviction-failed",
  Loading = "loading",
};

export enum TaskType {
  Player = "player",
  Ball = "ball",
};

export type TaskData = {
  task_id: string;
  last_frame_id: number;
  task_type: TaskType;
};

export type EvictionData = {
  status: EvictionStatus;
  eviction_failure_reason: string;
}

export type RunData = {
  eviction: EvictionData;
  tasks: TaskData[];
};

export type TState = {
  session?: TSession;
  labelTasks?: LabelTask[];
  runData?: RunData;
  rows: Array<TRow>;
  filteredRows: Array<TRow>;
  types: string[];
  periods: Array<string>;
  search: string;
  unfinishedOnly: boolean;
  mineOnly: boolean;
  unassignedOnly: boolean;
  fps: number;
  runId?: string;
  me?: string;
  columnWidths: Array<{ columnName: string; width: number | string }>;
};

export type UserFragment = {
  id: number;
  name?: string | null;
};

type TSessionLoadedAction = {
  type: 'session-loaded';
  payload: TSession | undefined;
};

type TLabelTasksLoadedAction = {
  type: 'label-tasks-loaded';
  payload: LabelTask[];
};

type TSetTaskOwner = {
  type: 'set-task-owner';
  payload: {
    owner: UserFragment | null;
    task: string;
  };
};

type TSetSearchAction = {
  type: 'set-search';
  payload: string;
};

type TSetColumnWidthsAction = {
  type: 'set-column-widths';
  payload: Array<{ columnName: string; width: number | string }>;
};

type TSetTaskTypesAction = {
  type: 'set-task-types';
  payload: string[];
};

type TSetPeriodsAction = {
  type: 'set-periods';
  payload: Array<string>;
};

type TSetUnfinishedOnlyAction = {
  type: 'set-unfinished-only';
  payload: boolean;
};

type TSetMineOnlyAction = {
  type: 'set-mine-only';
  payload: boolean;
};

type TSetUnassignedOnlyAction = {
  type: 'set-unassigned-only';
  payload: boolean;
};

type TTaskDataLoadedAction = {
  type: 'task-data-loaded';
  payload: RunData;
};

type TSetMeAction = {
  type: 'set-me';
  payload: string | undefined;
};

export type TAction =
  | TSessionLoadedAction
  | TLabelTasksLoadedAction
  | TSetSearchAction
  | TTaskDataLoadedAction
  | TSetTaskTypesAction
  | TSetPeriodsAction
  | TSetUnfinishedOnlyAction
  | TSetColumnWidthsAction
  | TSetTaskOwner
  | TSetMineOnlyAction
  | TSetMeAction
  | TSetUnassignedOnlyAction;

const team = (session: TSession | null | undefined, tlt: LabelTask) => {
  const classType: ClassType = (tlt.classType as ClassType) || ClassType.Referee;
  const isBall: boolean = classType === ClassType.Ball;
  const isOfficial: boolean = classType === ClassType.Referee;

  return isBall || isOfficial
    ? '-'
    : classType === ClassType.HomeKeeper || classType === ClassType.HomePlayer
    ? session?.homeTeam?.name ?? undefined
    : session?.awayTeam?.name ?? undefined;
};

const percentCompleted = (tasksData: TaskData[], tlt: LabelTask) => {
  let percCompleted: number = 0;
  const foundTaskData: TaskData | undefined = tasksData.find((td: TaskData) => String(td.task_id) === String(tlt.id));
  if (foundTaskData) {
    percCompleted = Math.max(
      0,
      Math.min(
        100,
        Math.floor(((foundTaskData.last_frame_id - tlt.startFrame) / (tlt.endFrame - tlt.startFrame)) * 100)
      )
    );
  }
  return percCompleted;
};

const type = (tlt: LabelTask) => {
  const classType: ClassType = (tlt.classType as ClassType) || ClassType.Referee;
  const isBall: boolean = classType === ClassType.Ball;
  const isOfficial: boolean = classType === ClassType.Referee;

  return isBall
    ? 'Ball'
    : isOfficial
    ? 'Official'
    : classType === ClassType.AwayKeeper || classType === ClassType.HomeKeeper
    ? 'Goalkeeper'
    : 'Player';
};

const playerIdentity = (tlt: LabelTask) => {
  const classType: ClassType = (tlt.classType as ClassType) || ClassType.Referee;
  const isOfficial: boolean = classType === ClassType.Referee;

  const lineupNumber: number | undefined = tlt.lineupEntry?.shirtNumber || undefined;
  const playerNumber: string = isOfficial ? '-' : lineupNumber ? lineupNumber.toString() : '';
  const playerName: string = isOfficial ? '-' : tlt.lineupEntry?.name || '';

  return isOfficial ? '-' : playerNumber ? playerNumber + '. ' + playerName : '';
};

const labelTaskToRow = (session: TSession | undefined, taskData: TaskData[], tlt: LabelTask): TRow => {
  const fps = (session?.videos && session?.videos.length > 0 && session?.videos[0].fps) || 25;
  return {
    id: String(tlt.id),
    period: PeriodTypeToString(findPeriodForFrames(session, tlt.startFrame, tlt.endFrame)?.type),
    team: team(session, tlt),
    progress: percentCompleted(taskData, tlt),
    type: type(tlt),
    startTime: secondsToHms(tlt.startFrame / fps),
    endTime: secondsToHms(tlt.endFrame / fps),
    identity: playerIdentity(tlt),
    owner: tlt.owner || null,
    tlt: tlt,
  };
};

export const initialState: TState = {
  session: undefined,
  labelTasks: undefined,
  runData: undefined,
  rows: [],
  filteredRows: [],
  search: '',
  types: [],
  periods: [],
  fps: 25,
  unfinishedOnly: false,
  mineOnly: false,
  unassignedOnly: false,
  columnWidths: [
    { columnName: 'progress', width: 100 },
    { columnName: 'id', width: 100 },
    { columnName: 'period', width: 140 },
    { columnName: 'startTime', width: 100 },
    { columnName: 'endTime', width: 100 },
    { columnName: 'team', width: 150 },
    { columnName: 'type', width: 100 },
    { columnName: 'identity', width: 150 },
    { columnName: 'owner', width: 200 },
    { columnName: 'flags', width: 150 },
  ],
};

export const reducer = (state: TState, action: TAction): TState => {
  return {
    session: sessionReducer(state.session, action),
    labelTasks: labelTasksReducer(state.labelTasks, action),
    fps: fpsReducer(state.fps, action),
    rows: rowsReducer(state.rows, action, state),
    filteredRows: filteredRowsReducer(state.filteredRows, action, state),
    search: searchReducer(state.search, action),
    types: typesReducer(state.types, action),
    periods: periodsReducer(state.periods, action),
    runData: taskDataReducer(state.runData, action),
    unfinishedOnly: unfinishedOnlyReducer(state.unfinishedOnly, action),
    unassignedOnly: unassignedOnlyReducer(state.unassignedOnly, action),
    mineOnly: mineOnlyReducer(state.mineOnly, action),
    columnWidths: columnWidthsReducer(state.columnWidths, action),
    me: meReducer(state.me, action),
  };
};

const meReducer = (state: string | undefined, action: TAction) => {
  if (action.type === 'set-me') {
    return action.payload;
  }
  return state;
};

const columnWidthsReducer = (state: Array<{ columnName: string; width: number | string }>, action: TAction) => {
  if (action.type === 'set-column-widths') {
    return action.payload;
  }
  return state;
};

const unfinishedOnlyReducer = (state: boolean, action: TAction) => {
  if (action.type === 'set-unfinished-only') {
    return action.payload;
  }
  return state;
};

const unassignedOnlyReducer = (state: boolean, action: TAction) => {
  if (action.type === 'set-unassigned-only') {
    return action.payload;
  }
  return state;
};

const mineOnlyReducer = (state: boolean, action: TAction) => {
  if (action.type === 'set-mine-only') {
    return action.payload;
  }
  return state;
};

const periodsReducer = (state: Array<string>, action: TAction) => {
  if (action.type === 'set-periods') {
    return action.payload;
  }
  return state;
};

const typesReducer = (state: string[], action: TAction) => {
  if (action.type === 'set-task-types') {
    return action.payload;
  }
  return state;
};

const taskDataReducer = (state: RunData | undefined, action: TAction) => {
  if (action.type === 'task-data-loaded') {
    return action.payload;
  }
  return state;
};

const labelTasksReducer = (state: LabelTask[] | undefined, action: TAction) => {
  if (action.type === 'label-tasks-loaded') {
    return action.payload;
  }
  return state;
};

const toRows = (session: TSession | undefined, labelTasks: LabelTask[] | undefined, taskData: TaskData[]) =>
  session && labelTasks ? labelTasks.map((tlt) => labelTaskToRow(session, taskData, tlt)) : [];

const sessionReducer = (state: TSession | undefined, action: TAction) => {
  if (action.type === 'session-loaded') {
    return action.payload;
  }
  return state;
};

const searchReducer = (state: string, action: TAction) => {
  if (action.type === 'set-search') {
    return action.payload;
  }
  return state;
};

const fpsReducer = (state: number, action: TAction) => {
  if (action.type === 'session-loaded') {
    return (
      (action.payload?.videos && action.payload?.videos.length > 0 && action.payload?.videos[0].fps) || 25
    );
  }
  return state;
};

const rowsReducer = (state: TRow[], action: TAction, fullState: TState) => {
  switch (action.type) {
    case 'session-loaded':
      return toRows(action.payload, fullState.labelTasks, fullState.runData?.tasks || []);
    case 'label-tasks-loaded':
      return toRows(fullState.session, action.payload, fullState.runData?.tasks || []);
    case 'set-task-owner':
      const found = state.find((row) => row.id === action.payload.task);
      if (found) {
        return [...state.filter((row) => row.id !== action.payload.task), { ...found, owner: action.payload.owner }];
      }
      return state;
    case 'task-data-loaded':
      return toRows(fullState.session, fullState.labelTasks, action.payload.tasks);
    default:
      return state;
  }
};

const filteredRowsReducer = (state: TRow[], action: TAction, fullState: TState) => {
  let { rows, search, types, periods, unfinishedOnly, mineOnly, unassignedOnly, me } = fullState;
  switch (action.type) {
    case 'session-loaded':
      rows = toRows(action.payload, fullState.labelTasks, fullState.runData?.tasks || []);
      break;
    case 'label-tasks-loaded':
      rows = toRows(fullState.session, action.payload, fullState.runData?.tasks || []);
      break;
    case 'task-data-loaded':
      rows = toRows(fullState.session, fullState.labelTasks, action.payload.tasks);
      break;
    case 'set-search':
      search = action.payload;
      break;
    case 'set-task-types':
      types = action.payload;
      break;
    case 'set-periods':
      periods = action.payload;
      break;
    case 'set-unfinished-only':
      unfinishedOnly = action.payload;
      break;
    case 'set-mine-only':
      mineOnly = action.payload;
      break;
    case 'set-unassigned-only':
      unassignedOnly = action.payload;
      break;
    case 'set-me':
      me = action.payload;
      break;
    case 'set-task-owner':
      const found = state.find((row) => row.id === action.payload.task);
      if (found) {
        rows = [...state.filter((row) => row.id !== action.payload.task), { ...found, owner: action.payload.owner }];
      }
      break;
    default:
      return state;
  }
  return filteredData(rows, search, types, periods, unfinishedOnly, mineOnly, me, unassignedOnly);
};

const filteredData = (
  rows: TRow[],
  search: string,
  types: string[],
  periods: string[],
  unfinishedOnly: boolean,
  mineOnly: boolean,
  me: string | undefined,
  unassignedOnly: boolean
) => {
  return rows
    .filter(searchFilter(search))
    .filter(typesFilter(types))
    .filter(periodsFilter(periods))
    .filter(unfinishedFilter(unfinishedOnly))
    .filter(mineFilter(mineOnly, me))
    .filter(unassignedFilter(unassignedOnly, me));
};

const unassignedFilter = (unassignedOnly: boolean, me: string | undefined) => (row: TRow) => {
  if (unassignedOnly) {
    return !row.owner;
  }
  return true;
};

const mineFilter = (mineOnly: boolean, me: string | undefined) => (row: TRow) => {
  if (mineOnly) {
    return row.owner ? String(row.owner.id) === me : false;
  }
  return true;
};

const unfinishedFilter = (unfinishedOnly: boolean) => (row: TRow) => {
  if (unfinishedOnly) {
    return row.progress !== 100;
  }
  return true;
};

const periodsFilter = (periods: string[]) => (row: TRow) => {
  if (periods.length > 0) {
    let ret = false;
    ret = ret || _.contains(periods, row.period);
    return ret;
  }
  return true;
};

const typesFilter = (types: string[]) => (row: TRow) => {
  if (types.length > 0) {
    let ret = false;
    ret = ret || _.contains(types, row.type);
    return ret;
  }
  return true;
};

const searchFilter = (search: string) => (row: TRow) => {
  let ret = false;
  if (!search || search === '') return true;
  let s = search?.toLowerCase() || '';
  if (!ret && row.identity.toLowerCase().indexOf(s) !== -1) ret = true;
  if (!ret && row.period.toLowerCase().indexOf(s) !== -1) ret = true;
  if (!ret && row.team?.toLowerCase().indexOf(s) !== -1) ret = true;
  if (!ret && row.id?.toString().indexOf(s) !== -1) ret = true;
  if (!ret && (row.owner?.name?.toLowerCase() || '').indexOf(s) !== -1) ret = true;
  return ret;
};

export const findPeriodForFrames = (session: TSession | undefined | null, startFrame: number, endFrame: number) => {
  const fps: number = (session?.videos && session?.videos.length > 0 && session?.videos[0].fps) || 25;
  const offsetSeconds: number = timeSpanToSeconds(session?.startAndFinishOffset);

  return (
    session?.periods &&
    session?.periods.find((p: Period) => {
      const periodStartFrame: number = Math.floor(((p.startTimeInMilliseconds || 0)/1000 - offsetSeconds) * fps);
      const periodEndFrame: number = Math.ceil(((p.endTimeInMilliseconds || 0)/1000 + offsetSeconds) * fps);
      return startFrame >= periodStartFrame && endFrame <= periodEndFrame;
    })
  );
};
